import { createApi } from "@reduxjs/toolkit/query/react";

import {
  candidatePortalAnonymousBaseQuery,
  candidatePortalBaseQuery,
} from "../../utils/authentication/fetchBaseQuery";
import {
  transformErrorResponse,
  transformSuccessResponse,
} from "../../utils/middleware/responseMiddleware2";

export const candidatePortalApi = createApi({
  reducerPath: "candidatePortalApi",
  baseQuery: candidatePortalBaseQuery,
  tagTypes: ["PortalProfile", "PortalActions", "PortalGrid"],
  endpoints: (builder) => ({
    getLinkedOrganizations: builder.query({
      query: () => ({ url: "/CandidatePortal/GetLinkedOrganizations" }),
      providesTags: ["PortalProfile"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    selectOrganization: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/SelectOrganization",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalProfile", "PortalActions", "PortalGrid"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    getProfile: builder.query({
      query: () => ({ url: "/CandidatePortal/GetProfile" }),
      providesTags: ["PortalProfile"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    updateProfile: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/UpdateProfile",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalProfile"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    requestContactChange: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/RequestContactChange",
        method: "POST",
        body,
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    verifyContactChange: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/VerifyContactChange",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalProfile"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    getPortalGrid: builder.query({
      query: (params) => ({
        url: params.url,
        params: params?.params,
      }),
      providesTags: ["PortalGrid"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    getApplication: builder.query({
      query: (applicantId) => ({
        url: "/CandidatePortal/GetApplication",
        params: { applicantId },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    getActionCenter: builder.query({
      query: () => ({ url: "/CandidatePortal/GetActionCenter" }),
      providesTags: ["PortalActions"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    respondToInterview: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/RespondToInterview",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalActions", "PortalGrid"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    applyToJob: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/ApplyToJob",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalActions", "PortalGrid"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    respondToOffer: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/RespondToOffer",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalActions", "PortalGrid"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    markNotificationRead: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/MarkNotificationRead",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalGrid"],
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    launchForm: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/LaunchForm",
        method: "POST",
        body,
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    launchAssessment: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortal/LaunchAssessment",
        method: "POST",
        body,
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
  }),
});

export const candidatePortalAuthApi = createApi({
  reducerPath: "candidatePortalAuthApi",
  baseQuery: candidatePortalAnonymousBaseQuery,
  endpoints: (builder) => ({
    requestPortalLogin: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortalAuth/RequestLoginChallenge",
        method: "POST",
        body,
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    verifyPortalLogin: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortalAuth/VerifyLoginChallenge",
        method: "POST",
        body,
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    logoutPortal: builder.mutation({
      query: (body) => ({
        url: "/CandidatePortalAuth/Logout",
        method: "POST",
        body,
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
  }),
});

export const {
  useGetLinkedOrganizationsQuery,
  useSelectOrganizationMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useRequestContactChangeMutation,
  useVerifyContactChangeMutation,
  useLazyGetPortalGridQuery,
  useGetApplicationQuery,
  useGetActionCenterQuery,
  useRespondToInterviewMutation,
  useApplyToJobMutation,
  useRespondToOfferMutation,
  useMarkNotificationReadMutation,
  useLaunchFormMutation,
  useLaunchAssessmentMutation,
} = candidatePortalApi;

export const {
  useRequestPortalLoginMutation,
  useVerifyPortalLoginMutation,
  useLogoutPortalMutation,
} = candidatePortalAuthApi;

